import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import type { Workbook } from 'exceljs';

import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  ActionIcon,
  Group,
  ScrollArea,
  Stack,
  Stepper,
  Table,
  Text,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import {
  IconCheck,
  IconCloudUpload,
  IconInfoCircle,
} from '@tabler/icons-react';

import ResponsiveStepper from '@ui-kit/molecules/ResponsiveStepper';
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import {
  useImportRequestListMutation,
  ListRequestListFoldersDocument,
} from '@app-types/blackroom-api';
import { ResourceMimeType } from '@app/types/blackroom-app/resourceMimeType';
import { FONT_WEIGHT } from '@ui-kit/fonts';
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import { palette } from '@ui-kit/colors';

import {
  DialogImportRequestListProps,
  ImportValidationError,
  ParsedRequestListItem,
} from './types';

const MIN_STEP = 0;
const MAX_STEP = 1;

function DialogImportRequestList(props: DialogImportRequestListProps) {
  const [currentStep, setCurrentStep] = useState(MIN_STEP);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [emptyFile, setEmptyFile] = useState(false);
  const [parsedItems, setParsedItems] = useState<ParsedRequestListItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    ImportValidationError[]
  >([]);

  const { t } = useTranslation();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();

  const [importRequestList, { loading: isImporting }] =
    useImportRequestListMutation();

  const nextStep = () =>
    setCurrentStep((current) => (current < MAX_STEP ? current + 1 : current));
  const prevStep = () =>
    setCurrentStep((current) => (current > MIN_STEP ? current - 1 : current));

  const downloadTemplate = useCallback(async () => {
    const { Workbook } = await import('exceljs');
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Request List');
    worksheet.columns = [
      { key: 'index', header: 'Index', width: 15 },
      { key: 'name', header: 'Name', width: 30 },
      { key: 'description', header: 'Description', width: 50 },
      { key: 'parentIndex', header: 'Parent Index', width: 15 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    worksheet.addRow({ index: '1', name: 'Legal Documents', description: '' });
    worksheet.addRow({
      index: '1.1',
      name: 'Articles of Incorporation',
      description: 'Founding documents',
      parentIndex: '1',
    });
    worksheet.addRow({
      index: '1.2',
      name: 'Bylaws',
      description: '',
      parentIndex: '1',
    });
    worksheet.addRow({
      index: '2',
      name: 'Financial Statements',
      description: '',
    });
    worksheet.addRow({
      index: '2.1',
      name: 'Annual Reports',
      description: 'Last 3 years',
      parentIndex: '2',
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: ResourceMimeType.xlsx });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'RequestListTemplate.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const isEmptyFile = useCallback(
    (workbook: Workbook) =>
      workbook.worksheets.every((worksheet) => worksheet.actualRowCount <= 1),
    [],
  );

  const handleValidateData = useCallback(async () => {
    if (!uploadedFile) return;

    if (emptyFile) setEmptyFile(false);
    const errors: ImportValidationError[] = [];
    const items: ParsedRequestListItem[] = [];
    const { Workbook } = await import('exceljs');
    const workbook = new Workbook();
    const data = await uploadedFile.arrayBuffer();

    const isXlsx =
      uploadedFile.name.endsWith('.xlsx') || uploadedFile.name.endsWith('.xls');

    if (isXlsx) {
      await workbook.xlsx.load(data);
    } else {
      const csvText = new TextDecoder().decode(data);
      const nodeCompat = {
        pipe<T extends { write(chunk: string): void; end(): void }>(dest: T) {
          dest.write(csvText);
          dest.end();
          return dest;
        },
      };
      await workbook.csv.read(
        nodeCompat as Parameters<typeof workbook.csv.read>[0],
      );
    }

    if (isEmptyFile(workbook)) {
      setEmptyFile(true);
      nextStep();
      return;
    }

    workbook.worksheets.forEach((worksheet) => {
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1) {
          const index = row.getCell(1).text?.trim();
          const name = row.getCell(2).text?.trim() || '';
          const description = row.getCell(3).text?.trim() || '';
          const parentIndex = row.getCell(4).text?.trim() || undefined;

          if (!index) {
            errors.push({
              rowNumber,
              columnHeader: 'Index',
              message: t(
                'RequestList:DialogImportRequestList.validationTable.errors.requiredValue',
              ),
            });
          } else if (!/^\d+(\.\d+)*$/.test(index)) {
            errors.push({
              rowNumber,
              columnHeader: 'Index',
              message: t(
                'RequestList:DialogImportRequestList.validationTable.errors.invalidIndex',
              ),
            });
          }

          if (!name) {
            errors.push({
              rowNumber,
              columnHeader: 'Name',
              message: t(
                'RequestList:DialogImportRequestList.validationTable.errors.requiredValue',
              ),
            });
          }

          items.push({ index: index || '', name, description, parentIndex });
        }
      });
    });

    setParsedItems(items);
    setValidationErrors(errors);
    nextStep();
  }, [uploadedFile, emptyFile, t, isEmptyFile]);

  const handleImport = useCallback(async () => {
    if (parsedItems.length === 0 || validationErrors.length > 0 || emptyFile)
      return;

    try {
      await importRequestList({
        variables: {
          input: {
            workspaceId: workspace.id,
            items: parsedItems.map((item) => ({
              index: item.index,
              name: item.name,
              description: item.description || undefined,
              parentIndex: item.parentIndex,
            })),
          },
        },
        refetchQueries: [ListRequestListFoldersDocument],
      });
      showNotification({
        title: t(
          'RequestList:DialogImportRequestList.successNotification.title',
        ),
        message: t(
          'RequestList:DialogImportRequestList.successNotification.description',
        ),
        color: 'blackroomGreen',
      });
      dialogStore?.closeDialog?.('dialog-request-list-import');
    } catch {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
    }
  }, [parsedItems, validationErrors, emptyFile, workspace.id, dialogStore, t]);

  function renderAlert() {
    if (emptyFile) {
      return (
        <Alert
          variant="light"
          color="blackroomRed"
          title={t(
            'RequestList:DialogImportRequestList.alerts.emptyFile.title',
          )}
          icon={<IconInfoCircle />}
          p="xs"
        >
          {t(
            'RequestList:DialogImportRequestList.alerts.emptyFile.description',
          )}
        </Alert>
      );
    }

    if (validationErrors.length > 0) {
      return (
        <Alert
          variant="light"
          color="blackroomRed"
          title={t('RequestList:DialogImportRequestList.alerts.error.title')}
          icon={<IconInfoCircle />}
          p="xs"
        >
          {t('RequestList:DialogImportRequestList.alerts.error.description')}
        </Alert>
      );
    }

    return (
      <Alert
        variant="light"
        color="blackroomGreen"
        title={t('RequestList:DialogImportRequestList.alerts.success.title')}
        icon={<IconCheck />}
        p="xs"
      >
        {t('RequestList:DialogImportRequestList.alerts.success.description')}
      </Alert>
    );
  }

  return (
    <Container w={600} fluid>
      <Box px="md" pb="md">
        <ResponsiveStepper active={currentStep}>
          <Stepper.Step
            label={t(
              'RequestList:DialogImportRequestList.steps.uploadFile.label',
            )}
          >
            <Dropzone
              accept={[
                ResourceMimeType.xlsx,
                'text/csv',
                'application/vnd.ms-excel',
              ]}
              multiple={false}
              onDrop={(files) => setUploadedFile(files[0])}
              maxSize={5 * 1024 ** 2}
            >
              <Center h="15vh">
                <Stack justify="center" align="center" gap="xs">
                  <ActionIcon
                    variant="transparent"
                    color="blackroomBlue"
                    aria-label="Upload"
                  >
                    <IconCloudUpload />
                  </ActionIcon>
                  <Text size="sm" fw={FONT_WEIGHT.lighter} ta="center">
                    {t(
                      'RequestList:DialogImportRequestList.steps.uploadFile.dropzonePlaceholder',
                    )}
                  </Text>
                </Stack>
              </Center>
            </Dropzone>
            {uploadedFile && (
              <Group gap={2} wrap="nowrap">
                <IconCheck
                  size={ICON_SIZE_MAP.small}
                  color={palette.blackroomGreen}
                />
                <Text c="blackroomGreen" pt={4} fz="sm" truncate>
                  {t(
                    'RequestList:DialogImportRequestList.steps.uploadFile.selectedFilePrefix',
                  )}{' '}
                  {uploadedFile.name}
                </Text>
              </Group>
            )}
            <Group pt="xs" justify="flex-end">
              <Button
                onClick={downloadTemplate}
                py={0}
                px={2}
                h="fit-content"
                variant="transparent"
                color="blackroomGrey"
                fz="sm"
                fw={300}
                style={{ textDecoration: 'underline' }}
              >
                {t('RequestList:DialogImportRequestList.ctaDownloadTemplate')}
              </Button>
            </Group>
          </Stepper.Step>

          <Stepper.Step
            label={t('RequestList:DialogImportRequestList.steps.preview.label')}
          >
            {validationErrors.length > 0 && (
              <Stack gap="sm">
                <ScrollArea.Autosize w="100%" mah={300}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          {t(
                            'RequestList:DialogImportRequestList.validationTable.columns.rowNumber.label',
                          )}
                        </Table.Th>
                        <Table.Th>
                          {t(
                            'RequestList:DialogImportRequestList.validationTable.columns.columnHeader.label',
                          )}
                        </Table.Th>
                        <Table.Th>
                          {t(
                            'RequestList:DialogImportRequestList.validationTable.columns.message.label',
                          )}
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {validationErrors.map((error, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>{error.rowNumber}</Table.Td>
                          <Table.Td>{error.columnHeader}</Table.Td>
                          <Table.Td>{error.message}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>
              </Stack>
            )}
            {renderAlert()}
          </Stepper.Step>
        </ResponsiveStepper>
      </Box>
      <ConfirmBar
        labelProps={{
          confirm:
            currentStep === MAX_STEP
              ? t('Common:cta.save')
              : t('Common:cta.saveContinue'),
          cancel:
            currentStep === MIN_STEP
              ? t('Common:cta.cancel')
              : t('Common:cta.back'),
        }}
        onConfirm={() => {
          if (uploadedFile && currentStep === MIN_STEP) {
            handleValidateData();
            return;
          }
          if (
            currentStep === MAX_STEP &&
            validationErrors.length === 0 &&
            !emptyFile
          ) {
            handleImport();
          }
        }}
        onCancel={() => {
          currentStep > MIN_STEP
            ? prevStep()
            : dialogStore?.closeDialog?.('dialog-request-list-import');
        }}
        disabled={
          (currentStep === MIN_STEP && !uploadedFile) ||
          (currentStep === MAX_STEP &&
            (validationErrors.length > 0 || emptyFile)) ||
          isImporting
        }
        saving={isImporting}
      />
    </Container>
  );
}

export default DialogImportRequestList;
